import { useLazyUser } from "$global-providers/user-provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { gql } from "@apollo/client";
import type {
  EpocusExamSyncPushDocument,
  EpocusNonBillableReason,
  QueryPullEpocusExamArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import { useMatch } from "react-router-dom";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusExam } from "../collections/epocus-exam";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusExamPullDocument,
  SyncEpocusExamPushDocument,
  type WholeSyncableEpocusExamFragment,
} from "./__generated__/epocus-exam.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_EXAM_REPLICATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateEpocusExam = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusExam>;
}) => {
  // pullEpocusExam takes exactly one of encounterId or patientId, so this pull
  // has two modes: an encounter being reviewed or performed, and the
  // patient-history page needing every exam on that patient (PNE-511). Both keys
  // come from the URL, mirroring useReplicateImagingInstance.
  const reviewEncounterMatch = useMatch(
    "/epocus/review/encounter/:encounterId",
  );
  const performerEncounterMatch = useMatch("/epocus/encounters/:encounterId/*");
  const performerEncounterId =
    performerEncounterMatch?.params.encounterId === "new"
      ? null
      : (performerEncounterMatch?.params.encounterId ?? null);
  const query = useQuerystring();
  const matchPat = useMatch("/dev-patient/:patientId/*");

  // The encounter context wins: it is the narrower scope and the one the user
  // is actively working in.
  const encounterId =
    reviewEncounterMatch?.params.encounterId ?? performerEncounterId;
  const patientId = encounterId
    ? null
    : (matchPat?.params.patientId ?? query.patientUuid ?? null);

  // The active filter key determines checkpoint storage and replication identity.
  const activeKey = encounterId ?? patientId;

  // The patient-scope mode fires on any patient page, not just the reviewer's
  // encounter route, so an unallowed role would turn every patient history into
  // a FORBIDDEN pull and the "Update failed" banner.
  const role = useLazyUser()?.role;
  const roleAllowed = isReplicationRoleAllowed(
    EPOCUS_EXAM_REPLICATION_ROLES,
    role,
  );

  const push = useMemo(
    () => ({
      mutation: SyncEpocusExamPushDocument,
      mapRxToGql: mapRxEpocusExamToGqlInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusExamFragment> | undefined
  >(() => {
    if (!activeKey || !roleAllowed) return undefined;
    return {
      query: SyncEpocusExamPullDocument,
      reSyncInterval: { minutes: 5 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullEpocusExamArgs,
        WholeSyncableEpocusExamFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            filters: encounterId ? { encounterId } : { patientId },
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: activeKey,
        db,
        collectionName: collection?.name,
      }),
    };
  }, [encounterId, patientId, activeKey, roleAllowed, db, collection]);

  return useGqlReplication<
    EpocusExam,
    WholeSyncableEpocusExamFragment,
    EpocusExamSyncPushDocument
  >({
    db,
    collection,
    push: roleAllowed ? push : undefined,
    pull,
    mapGqlToRx: mapGqlEpocusExamToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-exam",
  });
};

export const mapGqlEpocusExamToRx = (
  doc: WholeSyncableEpocusExamFragment,
): WithDeleted<EpocusExam> => ({
  id: doc.id,
  encounterId: doc.encounterId,
  examType: doc.examType as EpocusExam["examType"],
  worksheetData: doc.worksheetSummary as EpocusExam["worksheetData"],
  images: (doc.images ?? []) as EpocusExam["images"],
  status: doc.status as EpocusExam["status"],
  signedAt: doc.signedAt ?? null,
  signedByUserId: doc.signedByUserId ?? null,
  nonBillable: doc.nonBillable,
  nonBillableReason: (doc.nonBillableReason ??
    null) as EpocusExam["nonBillableReason"],
  amendments: (doc.amendments ?? []) as EpocusExam["amendments"],
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxEpocusExamToGqlInput = (
  doc: WithDeleted<EpocusExam>,
): EpocusExamSyncPushDocument => ({
  id: doc.id,
  encounterId: doc.encounterId,
  examType: doc.examType,
  worksheetSummary: doc.worksheetData,
  images: doc.images,
  status: doc.status,
  signedAt: doc.signedAt,
  signedByUserId: doc.signedByUserId,
  nonBillable: doc.nonBillable,
  nonBillableReason: doc.nonBillableReason as EpocusNonBillableReason | null,
  amendments: doc.amendments,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusExam on SyncableEpocusExam {
    id
    encounterId
    examType
    worksheetSummary
    images
    status
    signedAt
    signedByUserId
    nonBillable
    nonBillableReason
    amendments
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusExamPull($input: PullEpocusExamInput!) {
    pullEpocusExam(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusExam
      }
    }
  }
`;

gql`
  mutation SyncEpocusExamPush($input: PushEpocusExamInput!) {
    pushEpocusExam(input: $input) {
      rows {
        ...WholeSyncableEpocusExam
      }
    }
  }
`;
